import { EditorTheme } from '../templateEditor.theme'
import { clampRectToCanvas } from '../templateEditor.utils'
import { LineEnds, Rect } from '../templateEditor.types'

export const clientToLogical = (
    clientX: number,
    clientY: number,
    frameRect: DOMRect,
    displayScale: number,
    zoom: number
) => {
    const scale = displayScale * zoom
    return {
        x: (clientX - frameRect.left) / scale,
        y: (clientY - frameRect.top) / scale,
    }
}

export const clampLogicalPoint = (x: number, y: number, canvasW: number, canvasH: number) => ({
    x: Math.max(0, Math.min(canvasW, x)),
    y: Math.max(0, Math.min(canvasH, y)),
})

export const clampPlaceRect = (
    rect: Rect,
    canvasW: number,
    canvasH: number,
    minElSize: number,
    theme: Pick<EditorTheme, 'minElSize'>
) => clampRectToCanvas(rect, canvasW, canvasH, minElSize ?? theme.minElSize)

export const dragExceededThreshold = (startX: number, startY: number, endX: number, endY: number, threshold: number) =>
    Math.hypot(endX - startX, endY - startY) >= threshold

export const lineEndsFromDrag = (startX: number, startY: number, endX: number, endY: number, rect: Rect): LineEnds => ({
    x1: Math.max(0, Math.min(rect.w, startX - rect.x)),
    y1: Math.max(0, Math.min(rect.h, startY - rect.y)),
    x2: Math.max(0, Math.min(rect.w, endX - rect.x)),
    y2: Math.max(0, Math.min(rect.h, endY - rect.y)),
})

/** Горизонтальная линия по клику: рамка высотой stroke. */
export const collapseLinePlaceRect = (rect: Rect, stroke = 4, minWidth = 24): Rect => {
    const midY = rect.y + rect.h / 2
    const w = Math.max(rect.w, minWidth)
    return {
        x: rect.x,
        y: midY - stroke / 2,
        w,
        h: stroke,
    }
}

export const horizontalLineEnds = (rect: Rect): LineEnds => ({
    x1: 0,
    y1: rect.h / 2,
    x2: rect.w,
    y2: rect.h / 2,
})

/** Рамка и концы линии по двум точкам drag (диагональ / вертикаль / горизонталь). */
export const linePlacementFromDrag = (
    startX: number,
    startY: number,
    endX: number,
    endY: number,
    canvasW: number,
    canvasH: number,
    stroke = 4
): { rect: Rect; lineEnds: LineEnds } => {
    const x1 = Math.max(0, Math.min(canvasW, startX))
    const y1 = Math.max(0, Math.min(canvasH, startY))
    const x2 = Math.max(0, Math.min(canvasW, endX))
    const y2 = Math.max(0, Math.min(canvasH, endY))

    let minX = Math.min(x1, x2)
    let maxX = Math.max(x1, x2)
    let minY = Math.min(y1, y2)
    let maxY = Math.max(y1, y2)

    if (maxX - minX < stroke) {
        const mid = (minX + maxX) / 2
        minX = mid - stroke / 2
        maxX = mid + stroke / 2
    }
    if (maxY - minY < stroke) {
        const mid = (minY + maxY) / 2
        minY = mid - stroke / 2
        maxY = mid + stroke / 2
    }

    let w = Math.min(Math.max(maxX - minX, stroke), canvasW)
    let h = Math.min(Math.max(maxY - minY, stroke), canvasH)
    let x = Math.min(Math.max(minX, 0), Math.max(0, canvasW - w))
    let y = Math.min(Math.max(minY, 0), Math.max(0, canvasH - h))

    const rect = { x, y, w, h }
    return {
        rect,
        lineEnds: lineEndsFromDrag(x1, y1, x2, y2, rect),
    }
}
